import { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Loader2, Archive } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT");
const MS_PER_DAY = 86400000;
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / MS_PER_DAY));

function ConfirmButton({ label, icon: Icon, title, text, confirmLabel, onConfirm, testid, danger, busy }) {
  const cls = danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50";
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className={`h-8 gap-1 ${cls}`} disabled={busy} data-testid={testid}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{text}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction className={danger ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
            onClick={onConfirm} data-testid={`${testid}-confirm`}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ArchivedCard({ a, busy, onRestore, onPurge }) {
  const name = `${a.role === "medico" ? "Dr. " : ""}${a.nome} ${a.cognome}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`deleted-card-${a.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-heading font-700 text-slate-900">{name}</p>
          <p className="text-xs text-slate-500">{a.email}</p>
          <p className="text-xs text-slate-400">{a.role === "medico" ? a.specializzazione : a.azienda}</p>
        </div>
        <Badge className={a.role === "medico" ? "bg-sky-100 text-blue-700" : "bg-violet-100 text-violet-700"}>
          {a.role === "medico" ? "Medico" : "ISF"}
        </Badge>
      </div>
      <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
        <p>Eliminato il {fmt(a.deleted_at)}</p>
        <p data-testid={`deleted-appts-${a.id}`}>{a.appointments_total} appuntamenti in archivio, {a.appointments_future} futuri</p>
        <p className="font-600 text-amber-600">Cancellazione definitiva tra {daysLeft(a.expires_at)} giorni</p>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <ConfirmButton label="Elimina definitivamente" icon={Trash2} danger busy={busy} testid={`purge-${a.id}`}
          title="Eliminare definitivamente?" confirmLabel="Elimina definitivamente" onConfirm={onPurge}
          text={`I dati di ${name} saranno cancellati per sempre e non potranno più essere ripristinati.`} />
        <ConfirmButton label="Ripristina" icon={RotateCcw} busy={busy} testid={`restore-${a.id}`}
          title="Ripristinare l'account?" confirmLabel="Ripristina" onConfirm={onRestore}
          text={`L'account di ${name} tornerà attivo con appuntamenti, cronologia e disponibilità. Se un orario futuro è stato occupato nel frattempo, la nuova prenotazione verrà annullata e l'ISF avvisato.`} />
      </div>
    </div>
  );
}

export function DeletedAccountsTab({ onRestored }) {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/deleted");
    setItems(data.accounts);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, fn) => {
    setBusyId(id);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusyId(null);
    load();
  };

  const restore = (id) => act(id, async () => {
    const { data } = await api.post(`/admin/deleted/${id}/restore`);
    toast.success(`Account ripristinato: ${data.appointments} appuntamenti recuperati`);
    data.conflicts.forEach((c) => toast.warning(
      `Orario ${c.date} ${c.time} (${c.medico}) era occupato: annullata la prenotazione di ${c.isf}`, { duration: 10000 }));
    if (data.skipped > 0) toast.info(`${data.skipped} appuntamenti non ripristinati: la controparte non esiste più`);
    onRestored && onRestored();
  });
  const purge = (id) => act(id, async () => {
    await api.delete(`/admin/deleted/${id}`);
    toast.success("Account eliminato definitivamente");
  });

  if (items.length === 0) {
    return (
      <p className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 p-10 text-sm text-slate-400" data-testid="deleted-empty">
        <Archive className="h-4 w-4" /> Nessun account eliminato negli ultimi 30 giorni.
      </p>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="deleted-list">
      {items.map((a) => (
        <ArchivedCard key={a.id} a={a} busy={busyId === a.id} onRestore={() => restore(a.id)} onPurge={() => purge(a.id)} />
      ))}
    </div>
  );
}
